import React, { createContext, useContext, useMemo } from 'react';
import { Box, Card, CardContent, Typography, Stack, CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import dayjs from 'dayjs';
import { green, red, orange, grey } from '@mui/material/colors';

const STATUS_COLORS = {
  present: green[400],
  absent: red[300],
  leave: orange[400],
  weekend: grey[300],
};

const STATUS_LABEL = {
  present: 'Present',
  absent: 'Absent',
  leave: 'On leave',
  weekend: 'Weekend',
};

const AttendanceStatusContext = createContext(new Map());

const AttendancePickersDay = React.forwardRef((props, ref) => {
  const { day, outsideCurrentMonth, ...other } = props;
  const statusByDate = useContext(AttendanceStatusContext);
  const dateStr = dayjs(day).format('YYYY-MM-DD');
  const status = statusByDate.get(dateStr);
  const bg = status ? STATUS_COLORS[status] : null;
  const label = status ? STATUS_LABEL[status] : null;
  const title = !outsideCurrentMonth && label ? `${dateStr} · ${label}` : undefined;

  return (
    <PickersDay
      ref={ref}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      {...other}
      title={title}
      sx={[
        ...(Array.isArray(other.sx) ? other.sx : [other.sx].filter(Boolean)),
        !outsideCurrentMonth && bg
          ? {
              bgcolor: bg,
              color: 'common.white',
              fontWeight: 600,
              '&:hover, &:focus': { bgcolor: bg },
            }
          : null,
      ]}
    />
  );
});

AttendancePickersDay.displayName = 'AttendancePickersDay';

/**
 * @param {Object} props
 * @param {Array<{ date: string, status: string }>} props.days
 * @param {string|null} props.rangeStart - YYYY-MM-DD (optional, limits calendar navigation)
 * @param {string|null} props.rangeEnd - YYYY-MM-DD (optional)
 * @param {boolean} props.isLoading
 */
const EmployeeDashboardAttendanceCalendar = ({ days = [], rangeStart = null, rangeEnd = null, isLoading }) => {
  const statusByDate = useMemo(() => {
    const m = new Map();
    days.forEach((d) => m.set(d.date, d.status));
    return m;
  }, [days]);

  const referenceDate = useMemo(() => {
    if (rangeStart && dayjs(rangeStart).isValid()) {
      return dayjs(rangeStart);
    }
    if (rangeEnd && dayjs(rangeEnd).isValid()) {
      return dayjs(rangeEnd);
    }
    if (days[0]?.date) {
      return dayjs(days[0].date);
    }
    return dayjs();
  }, [rangeStart, rangeEnd, days]);

  const minDate = rangeStart && dayjs(rangeStart).isValid() ? dayjs(rangeStart) : undefined;
  const maxDate = rangeEnd && dayjs(rangeEnd).isValid() ? dayjs(rangeEnd) : undefined;

  const calendarKey = `${rangeStart ?? ''}|${rangeEnd ?? ''}`;

  const cardSx = { height: '100%', width: '100%', display: 'flex', flexDirection: 'column' };
  const contentSx = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    '&:last-child': { pb: 2 },
  };

  if (isLoading) {
    return (
      <Card sx={cardSx}>
        <CardContent sx={contentSx}>
          <Typography variant="h6" gutterBottom>
            Attendance (by day)
          </Typography>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 280,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!days.length) {
    return (
      <Card sx={cardSx}>
        <CardContent sx={contentSx}>
          <Typography variant="h6" gutterBottom>
            Attendance (by day)
          </Typography>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minHeight: 120 }}>
            <Typography variant="body2" color="text.secondary">
              No attendance in this period.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={cardSx}>
      <CardContent sx={contentSx}>
        <Typography variant="h6" gutterBottom>
          Attendance (by day)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '72ch' }}>
          Use the calendar header to change month. Day colors reflect attendance for that date.
        </Typography>

        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2, gap: 1, alignItems: 'center' }}>
          {Object.entries(STATUS_LABEL).map(([key, label]) => (
            <Stack key={key} direction="row" alignItems="center" spacing={0.75}>
              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: STATUS_COLORS[key] }} />
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            py: { xs: 0, sm: 1 },
            '& .MuiDateCalendar-root': {
              width: '100%',
              maxWidth: '100%',
            },
          }}
        >
          <AttendanceStatusContext.Provider value={statusByDate}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                key={calendarKey}
                readOnly
                value={null}
                referenceDate={referenceDate}
                minDate={minDate}
                maxDate={maxDate}
                disableHighlightToday={false}
                showDaysOutsideCurrentMonth
                slots={{ day: AttendancePickersDay }}
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  mx: 'auto',
                }}
              />
            </LocalizationProvider>
          </AttendanceStatusContext.Provider>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmployeeDashboardAttendanceCalendar;
